import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../axiosConfig";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import AutomatedMarkingResults, {
  exportStruggleAreasExcel,
  exportStruggleAreasPdf,
} from "./AutomatedMarkingResults.jsx";
import MarkingPaperAnnotations, {
  compressMarkedPdf, createAnnotatedPdfBytes,
  extractPdfFirstPageText,
  extractPdfPageTexts,
  snapAnnotationsToPdfText,
} from "./MarkingPaperAnnotations.jsx";
import { compressImageForUpload } from "../utils/uploadCompression";
import MarkingAllowance, { useMarkingAllowance } from "./MarkingAllowance.jsx";
import GlobalPencilLoader from "./GlobalPencilLoader.jsx";
import FeatureTour, { TourButton, useFeatureTour } from "./FeatureTour.jsx";
import HelpTip from "./HelpTip.jsx";
import "./AutomatedMarking.css";
import { matchLearnerFromIdentity, parseMarkingPayload, gradingSubjectMismatch, calculateMarkingTotals, findLearnerPageRanges } from "../utils/gradingPayload";
import useMarkingRecovery from './useMarkingRecovery';
import { getMarkingDocumentId, getMarkingDraftOwner } from '../utils/markingDraftStore';
import MemoPreparationReview from './MemoPreparationReview';
import { hasPendingRubricEvidence, reviewRubricCriterion } from '../utils/writingCriterionReview';

export { matchLearnerFromIdentity, parseMarkingPayload } from "../utils/gradingPayload";

// First-visit tours. Steps whose element isn't on screen are skipped.
const SETUP_TOUR = [
  { title: "Welcome to automated marking", body: "A one-minute tour of marking a class set. MarkSure suggests every mark; you check and approve them, so you stay in charge." },
  { target: ".marking-allowance", title: "Your scripts this month", body: "Each learner's script uses one from your plan. Marking the same script again within 24 hours is free, up to twice." },
  { target: "#marking-step-1", title: "1. Choose what you're marking", body: "Pick the subject, term and assessment. Approved marks are saved straight into that assessment in your mark book." },
  { target: "#marking-step-2", title: "2. Upload the scripts and memo", body: "Add one scan per learner, or one PDF of the whole class and MarkSure splits it. Then add the memo for this exact paper. For essays, upload the task and your rubric." },
  { target: "#marking-step-3", title: "3. Check and start", body: "Check the summary and press start. Several scripts are marked at once, and your progress is saved as you go." },
  { target: ".marking-about", title: "Help is always here", body: "Open 'How AI marking works' for the details, or press 'Take the tour' to see this again." },
];

const REVIEW_TOUR = [
  { target: ".studio-batch-nav", title: "Move between scripts", body: "Each learner's script has its own page. Use the arrows or the list to switch learners." },
  { target: ".marking-paper-card", title: "The learner's script, marked", body: "Green ticks for marks earned, red crosses for marks lost, beside each answer. Drag a tick to move it, double-click to flip it, right-click to remove it." },
  { target: "select[aria-label='Jump to question']", title: "Go question by question", body: "Each question shows the learner's answer next to your memo, with the suggested mark. Use − and + to change any mark you disagree with." },
  { target: "[data-tour='next-unchecked']", title: "Only check what's flagged", body: "Answers that match your memo (and clearly wrong letters or numbers) are checked automatically. Only the ones that need your judgement stay flagged, each with the reason. 'Next unchecked' takes you straight to them." },
  { target: ".studio-approval-checkbox", title: "Approve when you're happy", body: "Tick 'I've checked these marks', then 'Approve & save marks'. Nothing goes into your mark book until you do." },
];

const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.webp";
const MAX_FILE_SIZE = 25 * 1024 * 1024;
export const MEMO_EXTRACTION_REVISION = 'rubric-criteria-3';

// Open a marked script on the first question the teacher still has to check.
function firstToReview(marking) {
  const index = (marking?.results || []).findIndex((result) => result.needsReview);
  return index >= 0 ? index : 0;
}

function normalizeQuestionKeyClient(questionNumber) {
  return String(questionNumber || "")
    .trim()
    .toLowerCase()
    .replace(/^question\s+/i, "")
    .replace(/[.:)]+$/, "")
    .trim();
}

export function mapServerAnnotationsToClient(suggestedAnnotations = []) {
  if (!Array.isArray(suggestedAnnotations)) {
    return [];
  }
  return suggestedAnnotations.map((annotation) => {
    const isNotAwarded =
      annotation.status === "not_awarded" || annotation.awarded === false;
    const rawX = annotation.tickPoint?.x ?? annotation.x;
    const rawY = annotation.tickPoint?.y ?? annotation.y;
    const needsPlacement =
      Boolean(annotation.needsPlacement) ||
      rawX === null ||
      rawY === null ||
      rawX === undefined ||
      rawY === undefined ||
      !Number.isFinite(Number(rawX)) ||
      !Number.isFinite(Number(rawY));
    const resolvedX = needsPlacement ? null : Number(rawX);
    const resolvedY = needsPlacement ? null : Number(rawY);
    return {
      ...annotation,
      x: resolvedX,
      y: resolvedY,
      tickPoint: needsPlacement ? null : { x: resolvedX, y: resolvedY },
      evidenceBox: annotation.evidenceBox || null,
      needsPlacement,
      type: isNotAwarded ? "lost" : "suggested",
      source: annotation.source || "ai",
      sourceCoordinate: annotation.sourceCoordinate || "ai_tick_point",
    };
  });
}

async function appendLearnerDocumentToFormData(
  formData,
  file,
  memoFile = null,
) {
  if (memoFile) {
    formData.append("answer_memo", memoFile);
  }
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
    // The backend reads the original PDF. Rendering every page here adds a
    // second, unnecessary preparation stage that can stall long writing scripts.
    formData.append("learner_exam", file);
    return;
  }
  // Photos are resized/upright-rotated copies; annotations still draw on the original file.
  const upload = await compressImageForUpload(file);
  formData.append("learner_exam", upload, upload.name);
}

async function postProcessMarking(apiUrl, buildFormData, request = {}) {
  const data = await buildFormData();
  if (request.owner && getMarkingDraftOwner(apiUrl) !== request.owner) throw new Error('The marking session was signed out. Sign in again to resume.');
  const response = await axios.post(`${apiUrl}/process_marking.php`, data, { timeout: 360000, signal: request.signal });
  return parseMarkingPayload(response.data);
}

export function optionalPdfResult(promise, fallback, timeoutMs = 10000) {
  let timer;
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise(resolve => { timer = setTimeout(() => resolve(fallback), timeoutMs); }),
  ]).finally(() => clearTimeout(timer));
}

function FileDropzone({ label, file, onFileChange, inputId, onHelpClick, disabled = false }) {
  const inputRef = useRef(null);
  const chooseFile = (candidate) => {
    if (!candidate || disabled) return;
    if (
      !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(
        candidate.type,
      )
    ) {
      onFileChange(null, "Please choose a PDF, JPEG, PNG, or WebP file.");
      return;
    }
    if (candidate.size > MAX_FILE_SIZE) {
      onFileChange(null, "Each file must be 25 MB or smaller.");
      return;
    }
    onFileChange(candidate, "");
  };

  return (
    <div className="marking-upload-field">
      <div className="marking-label-with-hint">
        <label htmlFor={inputId}>{label}</label>
        {onHelpClick && (
          <button
            type="button"
            className="btn-inline-scan-help"
            onClick={onHelpClick}
            title="Click to view step-by-step scanning instructions"
          >
            Scanning help
          </button>
        )}
      </div>
      <div
        className={`marking-dropzone${file ? " has-file" : ""}`}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " "))
            inputRef.current?.click();
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          chooseFile(event.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          disabled={disabled}
          accept={ACCEPTED_TYPES}
          hidden
          onChange={(event) => chooseFile(event.target.files?.[0])}
        />
        <strong>{file ? file.name : "Drop a scanned document here"}</strong>
        <span>
          {file
            ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
            : "or click to browse · PDF, JPG, PNG, WebP"}
        </span>
      </div>
    </div>
  );
}

// Same pencil-writes-a-tick loader as the rest of the app, sized to sit inside a panel.
export function AiGeneratingLoader() {
  return (
    <div className="ai-loader-stage" aria-label="AI Generating marks">
      <GlobalPencilLoader isInline text="Marking with AI…" />
    </div>
  );
}

export default function AutomatedMarking() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { allowance, refresh: refreshAllowance } = useMarkingAllowance(apiUrl);
  const [examFile, setExamFile] = useState(null);
  const [examFiles, setExamFiles] = useState([]);
  const [memoFile, setMemoFile] = useState(null);
  const [memoPreparation, setMemoPreparation] = useState(null);
  const [memoAssessmentConfirmation, setMemoAssessmentConfirmation] = useState('');
  const [assessmentType, setAssessmentType] = useState('auto');
  const [fieldErrors, setFieldErrors] = useState({ exam: "", memo: "" });
  const [marking, setMarking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingMarks, setSavingMarks] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [learners, setLearners] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selection, setSelection] = useState({
    subjectId: "",
    term: "1",
    studentId: "",
    assessmentId: "",
  });
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [batchMode, setBatchMode] = useState(true);
  const [batchItems, setBatchItems] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [paperFile, setPaperFile] = useState(null);
  const [paperAnnotations, setPaperAnnotations] = useState([]);
  const [activeBatchIndex, setActiveBatchIndex] = useState(null);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [targetPage, setTargetPage] = useState(null);
  const [setupCollapsed, setSetupCollapsed] = useState(false);
  const [singlePdfInfo, setSinglePdfInfo] = useState(null);
  const [showScanGuide, setShowScanGuide] = useState(false);
  const [scanGuideTab, setScanGuideTab] = useState("copier");
  const [loadingState, setLoadingState] = useState({
    percent: 0,
    stepIndex: 0,
    stageLabel: "Preparing script pages…",
    elapsedSeconds: 0,
  });
  const [bulkDownloadState, setBulkDownloadState] = useState({
    loading: false,
    progress: "",
    error: "",
  });
  const learnerSelectRef = useRef(null);
  const memoCacheRef = useRef({ key: "", promise: null, data: null });
  const processingContextRef = useRef(null);
  const draftSnapshot = useMemo(() => ({
    examFile, examFiles, memoFile, assessmentType, marking, selection, approved, batchMode, batchItems, batchProgress,
    memoPreparation: memoPreparation?.review ? memoPreparation : null, memoAssessmentConfirmation,
    paperFile, paperAnnotations, activeBatchIndex, activeQuestionIndex, targetPage, setupCollapsed, singlePdfInfo,
    inProgress: loading, preparedMemo: memoCacheRef.current.data ? { key: memoCacheRef.current.key, data: memoCacheRef.current.data, revision: memoCacheRef.current.revision } : null,
  }), [examFile, examFiles, memoFile, assessmentType, marking, selection, approved, batchMode, batchItems, batchProgress,
    paperFile, paperAnnotations, activeBatchIndex, activeQuestionIndex, targetPage, setupCollapsed, singlePdfInfo, loading, memoPreparation, memoAssessmentConfirmation]);
  const applyRecovery = (state, activeRun) => {
    if (state.preparedMemo?.data) memoCacheRef.current = { ...state.preparedMemo, promise: null };
    setMemoPreparation(state.preparedMemo?.revision === MEMO_EXTRACTION_REVISION
      ? { key: state.preparedMemo.key, data: state.preparedMemo.data }
      : state.memoPreparation?.review ? state.memoPreparation : null);
    setMemoAssessmentConfirmation(state.memoAssessmentConfirmation || '');
    processingContextRef.current = state.processingContext || null;
    setExamFile(state.examFile || null); setExamFiles(Array.isArray(state.examFiles) ? state.examFiles : []);
    setMemoFile(state.memoFile || null); setAssessmentType(state.assessmentType === 'question' ? 'auto' : state.assessmentType || 'auto');
    const completedIndex = !state.marking && !activeRun && state.batchMode !== false ? (state.batchItems || []).findIndex(item => item?.marking) : -1;
    const completed = completedIndex >= 0 ? state.batchItems[completedIndex] : null;
    setMarking(state.marking || completed?.marking || null);
    setPaperFile(completed ? state.examFiles?.[completedIndex] || null : state.paperFile || null);
    setSelection(state.selection || { subjectId: '', term: '1', studentId: '', assessmentId: '' });
    setApproved(Boolean(state.approved)); setBatchMode(state.batchMode !== false);
    setBatchItems(Array.isArray(state.batchItems) ? state.batchItems : []);
    setBatchProgress(state.batchProgress || { current: 0, total: 0 });
    setPaperAnnotations(completed ? completed.annotations || [] : Array.isArray(state.paperAnnotations) ? state.paperAnnotations : []);
    setActiveBatchIndex(completed ? completedIndex : state.activeBatchIndex ?? null); setActiveQuestionIndex(state.activeQuestionIndex || 0);
    if (completed) setSelection({ ...state.selection, studentId: completed.studentId || '' });
    setTargetPage(state.targetPage || null); setSetupCollapsed(Boolean(state.setupCollapsed || completed));
    setSinglePdfInfo(state.singlePdfInfo ? { ...state.singlePdfInfo, isSplitting: false } : null);
    setLoading(activeRun);
    if (activeRun) setLoadingState(current => ({ ...current, stepIndex: 2, percent: Math.max(32, current.percent), stageLabel: 'Continuing the saved marking batch…' }));
  };
  const recovery = useMarkingRecovery(apiUrl, draftSnapshot, applyRecovery);

  const getMemoCacheKey = (file) =>
    file ? `memo-v2:${assessmentType}:${getMarkingDocumentId(file)}` : "";

  const ensureMemoProfile = (file) => {
    if (!file) return Promise.resolve(null);
    const key = getMemoCacheKey(file);
    const generation = recovery.controller?.generation;
    const canCache = () => memoCacheRef.current.key === key && (!recovery.controller ||
      (recovery.controller.generation === generation && getMarkingDocumentId(recovery.controller.state?.memoFile) === getMarkingDocumentId(file)
        && (recovery.controller.state?.assessmentType || 'auto') === assessmentType));
    const existing = recovery.controller?.memoRequests.get(key);
    if (existing) {
      memoCacheRef.current = { key, data: null, promise: existing, revision: MEMO_EXTRACTION_REVISION };
      return existing.then(data => {
        if (!canCache()) throw new Error('The memo changed during preparation. Start again with the current memo.');
        memoCacheRef.current = { key, data, promise: null, revision: MEMO_EXTRACTION_REVISION };
        setMemoPreparation({ key, data });
        return data;
      });
    }
    if (memoCacheRef.current.key === key && memoCacheRef.current.revision === MEMO_EXTRACTION_REVISION) {
      if (memoCacheRef.current.data) {
        return Promise.resolve(memoCacheRef.current.data);
      }
      if (memoCacheRef.current.promise) {
        return memoCacheRef.current.promise;
      }
    }
    setMemoPreparation({ key, loading: true });
    const promise = compressImageForUpload(file)
      .then((upload) => {
        const data = new FormData();
        data.append("answer_memo", upload, upload.name);
        data.append("assessment_type", assessmentType);
        return axios.post(`${apiUrl}/prepare_marking_memo.php`, data, { timeout: 210000 });
      })
      .then((memoResponse) => {
        if (
          memoResponse.data &&
          typeof memoResponse.data === "object" &&
          Array.isArray(memoResponse.data.questions)
        ) {
          if (!canCache()) throw new Error('The memo changed during preparation. Start again with the current memo.');
          if (canCache()) {
            memoCacheRef.current = { key, promise: null, data: memoResponse.data, revision: MEMO_EXTRACTION_REVISION };
            setMemoPreparation({ key, data: memoResponse.data });
            recovery.controller?.patch({ preparedMemo: { key, data: memoResponse.data, revision: MEMO_EXTRACTION_REVISION } });
          }
          return memoResponse.data;
        }
        if (canCache()) memoCacheRef.current = { key, promise: null, data: null };
        throw new Error('The memo or writing rubric did not contain a usable marking profile. Upload the complete task and rubric.');
      })
      .catch((requestError) => {
        const message = requestError.response?.data?.error ||
          (requestError.code === 'ECONNABORTED'
            ? 'Preparing the memo or writing rubric timed out. Please retry with a clear, complete rubric.'
            : requestError.message || 'The memo or writing rubric could not be prepared.');
        if (canCache()) {
          memoCacheRef.current = { key, promise: null, data: null };
          const preparation = { key, review: requestError.response?.data?.review || { status: 'needs_review', message, questions: [] } };
          setMemoPreparation(preparation);
          recovery.controller?.patch({ memoPreparation: preparation, preparedMemo: null });
        }
        throw new Error(message);
      });
    memoCacheRef.current = { key, promise, data: null, revision: MEMO_EXTRACTION_REVISION };
    recovery.controller?.memoRequests.set(key, promise);
    promise.finally(() => recovery.controller?.memoRequests.delete(key)).catch(() => {});
    return promise;
  };

  const handleBatchFilesSelect = async (event) => {
    const rawFiles = Array.from(event.target.files || []).filter(
      (file) => file.size <= MAX_FILE_SIZE,
    );
    setExamFiles(rawFiles);
    if (rawFiles.length > 0) {
      setPaperFile(rawFiles[0]);
      setActiveBatchIndex(0);
    }
    setFieldErrors((current) => ({
      ...current,
      exam: rawFiles.length ? "" : "Choose at least one file under 25 MB.",
    }));

    if (
      rawFiles.length === 1 &&
      (rawFiles[0].type === "application/pdf" ||
        rawFiles[0].name.toLowerCase().endsWith(".pdf"))
    ) {
      try {
        const arrayBuffer = await rawFiles[0].arrayBuffer();
        const doc = await PDFDocument.load(arrayBuffer);
        const pageCount = doc.getPageCount();
        if (pageCount > 1) {
          setSinglePdfInfo({
            file: rawFiles[0],
            fileName: rawFiles[0].name,
            pageCount,
            pagesPerStudent: 1,
            isSplitting: false,
          });
          return;
        }
      } catch {
        // Ignore PDF read error
      }
    }
    setSinglePdfInfo(null);
  };

  const handleSplitCombinedPdf = async () => {
    if (!singlePdfInfo?.file) return;
    setSinglePdfInfo((prev) => (prev ? { ...prev, isSplitting: true } : null));
    try {
      const arrayBuffer = await singlePdfInfo.file.arrayBuffer();
      const srcDoc = await PDFDocument.load(arrayBuffer);
      const totalPages = srcDoc.getPageCount();
      const perStudent = Math.max(1, singlePdfInfo.pagesPerStudent);
      const splitList = [];
      const baseName = singlePdfInfo.file.name.replace(/\.pdf$/i, "");
      const detectedRanges = findLearnerPageRanges(await extractPdfPageTexts(singlePdfInfo.file));
      const ranges = detectedRanges || Array.from({ length: Math.ceil(totalPages / perStudent) }, (_, index) => ({
        startPage: index * perStudent, endPage: Math.min((index + 1) * perStudent, totalPages),
      }));
      for (const { startPage, endPage } of ranges) {
        const newDoc = await PDFDocument.create();
        const indices = [];
        for (let p = startPage; p < endPage; p++) {
          indices.push(p);
        }
        const copiedPages = await newDoc.copyPages(srcDoc, indices);
        copiedPages.forEach((cp) => newDoc.addPage(cp));
        const pdfBytes = await newDoc.save();
        const studentNum = splitList.length + 1;
        const rangeLabel =
          endPage - startPage === 1
            ? `p${startPage + 1}`
            : `p${startPage + 1}-${endPage}`;
        const studentFile = new File(
          [pdfBytes],
          `${baseName}_Student_${studentNum}_(${rangeLabel}).pdf`,
          { type: "application/pdf" },
        );
        splitList.push(studentFile);
      }

      setExamFiles(splitList);
      if (splitList.length > 0) {
        setPaperFile(splitList[0]);
        setActiveBatchIndex(0);
      }
      setSinglePdfInfo(null);
    } catch (err) {
      setError(
        `Could not split the combined PDF: ${err?.message || "Error occurred"}`,
      );
      setSinglePdfInfo((prev) =>
        prev ? { ...prev, isSplitting: false } : null,
      );
    }
  };

  useEffect(() => {
    let active = true;
    if (memoFile && recovery.ready && !recovery.recovered) {
      ensureMemoProfile(memoFile).catch(err => { if (active) setError(err.message); });
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoFile, apiUrl, assessmentType, recovery.ready, recovery.recovered]);

  useEffect(() => {
    if (!loading) {
      return undefined;
    }
    const aiSubStages = [
      "Reading learner name and cover details…",
      "Checking answers, underlined words & parts of speech…",
      "Evaluating extended writing, dialogues & method steps…",
      "Calculating marks and generating feedback…",
    ];
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - startedAt) / 1000),
      );
      setLoadingState((curr) => {
        let nextPercent = curr.percent;
        let nextStageLabel = curr.stageLabel;
        if (curr.stepIndex === 0) {
          nextPercent = Math.min(16, curr.percent + 2);
        } else if (curr.stepIndex === 1) {
          nextPercent = Math.min(30, Math.max(18, curr.percent + 2));
        } else if (curr.stepIndex === 2) {
          const delta = curr.percent < 65 ? 3 : curr.percent < 80 ? 1.5 : 0.5;
          nextPercent = Math.min(88, Math.max(32, curr.percent + delta));
          const phaseIdx = Math.min(
            aiSubStages.length - 1,
            Math.floor(Math.max(0, nextPercent - 32) / 14),
          );
          nextStageLabel = aiSubStages[phaseIdx];
        } else if (curr.stepIndex >= 3) {
          nextPercent = Math.min(97, Math.max(90, curr.percent + 1.5));
        }
        return {
          ...curr,
          percent: Math.round(nextPercent),
          stageLabel: nextStageLabel,
          elapsedSeconds,
        };
      });
    }, 650);
    return () => clearInterval(interval);
  }, [loading]);

  const batchSummary = batchItems.reduce(
    (summary, item) => {
      if (!item.marking?.results) return summary;
      const awarded = Number(item.marking.totalAwarded || 0);
      const maximum = Number(item.marking.totalMarks || 0);
      summary.processed += 1;
      summary.awarded += awarded;
      summary.maximum += maximum;
      summary.percentages.push(maximum > 0 ? (awarded / maximum) * 100 : 0);
      return summary;
    },
    { processed: 0, awarded: 0, maximum: 0, percentages: [] },
  );

  const selectedSubject = subjects.find(
    (s) => String(s.subject_id || s.id) === String(selection.subjectId),
  );
  const selectedAssessment = assessments.find(
    (a) => String(a.id) === String(selection.assessmentId),
  );
  const memoAssessmentKey = profile => `${getMemoCacheKey(memoFile)}:${selection.subjectId}:${selection.term}:${selection.assessmentId}:${selectedAssessment?.max_mark}:${profile?.totalMarks}`;
  const assertMemoReady = profile => {
    if (profile.memoValidation?.status === 'needs_confirmation') {
      throw new Error('Check and confirm the memo allocations and total before marking.');
    }
    if (Number(selectedAssessment?.max_mark) > 0 && Math.abs(Number(selectedAssessment.max_mark) - Number(profile.totalMarks)) > .001
        && memoAssessmentConfirmation !== memoAssessmentKey(profile)) {
      throw new Error('The memo total differs from the selected assessment section. Check and confirm the selection before marking.');
    }
  };
  const confirmMemoTotal = () => {
    if (!memoPreparation?.data || memoPreparation.key !== getMemoCacheKey(memoFile)) return;
    const data = { ...memoPreparation.data, memoValidation: { ...memoPreparation.data.memoValidation,
      status: 'teacher_confirmed', confirmedTotal: memoPreparation.data.totalMarks } };
    memoCacheRef.current = { ...memoCacheRef.current, data };
    setMemoPreparation({ key: memoPreparation.key, data }); setError('');
    recovery.controller?.patch({ preparedMemo: { key: memoPreparation.key, data, revision: MEMO_EXTRACTION_REVISION } });
  };
  const retryMemoPreparation = () => {
    const key = getMemoCacheKey(memoFile);
    setError('');
    ensureMemoProfile(memoFile).catch(err => { if (memoCacheRef.current.key === key) setError(err.message); });
  };
  const selectedLearner = learners.find(
    (l) => String(l.student_id) === String(selection.studentId),
  );

  const activeQuestionNumber =
    marking?.results?.[activeQuestionIndex]?.questionNumber || null;
  const reviewedQuestions = marking?.results
    ? marking.results.filter((result) => !result.needsReview).length
    : 0;
  const reviewQuestions = marking?.results
    ? marking.results.length - reviewedQuestions
    : 0;
  const percentage =
    marking && marking.totalMarks > 0
      ? (marking.totalAwarded / marking.totalMarks) * 100
      : 0;

  const activePaperFile =
    paperFile ||
    (batchMode
      ? examFiles[activeBatchIndex ?? 0] || examFiles[0] || null
      : examFile);
  const isStudioActive = Boolean(activePaperFile || marking);
  const concurrency = Math.min(6, examFiles.length);
  const setupTour = useFeatureTour("marking-setup", !isStudioActive && !loading);
  const reviewTour = useFeatureTour("marking-review", Boolean(marking) && !loading);

  const handleSelectQuestionFromPaper = (questionNumber) => {
    if (!marking?.results) return;
    const targetKey = normalizeQuestionKeyClient(questionNumber);
    const index = marking.results.findIndex(
      (item) => normalizeQuestionKeyClient(item.questionNumber) === targetKey,
    );
    if (index >= 0) {
      setActiveQuestionIndex(index);
    }
  };
  const selectReviewQuestion = (index) => {
    setActiveQuestionIndex(index);
    const questionNumber = marking?.results?.[index]?.questionNumber;
    if (questionNumber) setTargetPage({ questionNumber, ts: Date.now() });
  };

  const switchBatchScript = (index) => {
    const item = batchItems[index];
    setSelection((current) => ({ ...current, studentId: item?.studentId || "" }));
    if (examFiles[index]) {
      setPaperFile(examFiles[index]);
      setActiveBatchIndex(index);
    }
    if (!item?.marking) {
      setMarking(null);
      setPaperAnnotations([]);
      return;
    }
    setMarking(item.marking);
    setActiveBatchIndex(index);
    setPaperFile(examFiles[index] || null);
    setPaperAnnotations(item.annotations || []);
    setApproved(Boolean(item.marking?.saved));
    setError("");
    setSaveStatus("");
    setSetupCollapsed(true);
    setActiveQuestionIndex(firstToReview(item.marking));
  };

  const prepareApprovedState = () => {
    const nextAnnotations = paperAnnotations.map((ann) => ({
      ...ann,
      type: ann.type === "suggested" ? "confirmed" : ann.type,
    }));
    const nextResults = Array.isArray(marking?.results)
      ? marking.results.map((item) => ({ ...item, needsReview: false }))
      : [];
    setApproved(true);
    setPaperAnnotations(nextAnnotations);
    setMarking((current) =>
      current ? { ...current, results: nextResults } : current,
    );
    if (batchMode && activeBatchIndex !== null) {
      setBatchItems((current) =>
        current.map((item, idx) =>
          idx === activeBatchIndex && item.marking
            ? {
                ...item,
                annotations: nextAnnotations,
                marking: { ...item.marking, results: nextResults },
              }
            : item,
        ),
      );
    }
    return { nextAnnotations, nextResults };
  };

  const downloadAllBatchMarkedZip = async () => {
    if (!batchItems || batchItems.length === 0) return;
    setBulkDownloadState({
      loading: true,
      progress: "Preparing ZIP archive…",
      error: "",
    });
    try {
      const zip = new JSZip();
      const subjectTag = selectedSubject
        ? `${selectedSubject.name}_Gr${selectedSubject.grade}`
        : "Subject";
      const assessTag = selectedAssessment
        ? selectedAssessment.section_name
        : "Assessment";
      const cleanBaseName = `${subjectTag}_${assessTag}_Term${selection.term || 1}`.replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      );

      for (let i = 0; i < batchItems.length; i += 1) {
        const item = batchItems[i];
        const rawFile = examFiles[i];
        if (!item?.marking || !rawFile) continue;

        const studentName =
          item.learnerName || item.marking?.learnerName || `Learner_${i + 1}`;
        setBulkDownloadState({
          loading: true,
          progress: `Stamping script ${i + 1} of ${batchItems.length} (${studentName})…`,
          error: "",
        });

        const annotations =
          Array.isArray(item.annotations) && item.annotations.length > 0
            ? item.annotations
            : mapServerAnnotationsToClient(
                item.marking?.suggestedAnnotations || [],
              );

        const pdfBytes = await createAnnotatedPdfBytes({
          file: rawFile,
          annotations,
          marking: item.marking,
          learnerName: item.marking?.learnerName || item.marking?.learner || item.learnerName || "",
          schoolName: item.marking?.school || "",
          subjectName: item.marking?.subjectName || item.marking?.subject || (selectedSubject ? `${selectedSubject.name} (Grade ${selectedSubject.grade})` : ""),
          assessmentName: selectedAssessment
            ? `${selectedAssessment.section_name} (Term ${selection.term})`
            : "",
          pdfStampMode: "cover",
        });

        const safeSlug = studentName.replace(/[^a-zA-Z0-9_-]/g, "_");
        const outFileName = `Script_${String(i + 1).padStart(2, "0")}_${safeSlug}_marked.pdf`;
        zip.file(outFileName, pdfBytes);
      }

      setBulkDownloadState({
        loading: true,
        progress: "Packaging ZIP file…",
        error: "",
      });
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${cleanBaseName}_All_Marked_Scripts.zip`);
    } catch (err) {
      setBulkDownloadState({
        loading: false,
        progress: "",
        error: err?.message || "Failed to create bulk marked scripts ZIP.",
      });
      return;
    }
    setBulkDownloadState({ loading: false, progress: "", error: "" });
  };

  const downloadAllBatchCombinedPdf = async () => {
    if (!batchItems || batchItems.length === 0) return;
    setBulkDownloadState({
      loading: true,
      progress: "Preparing combined PDF…",
      error: "",
    });
    try {
      const masterPdf = await PDFDocument.create();
      const subjectTag = selectedSubject
        ? `${selectedSubject.name}_Gr${selectedSubject.grade}`
        : "Subject";
      const assessTag = selectedAssessment
        ? selectedAssessment.section_name
        : "Assessment";
      const cleanBaseName = `${subjectTag}_${assessTag}_Term${selection.term || 1}`.replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      );

      for (let i = 0; i < batchItems.length; i += 1) {
        const item = batchItems[i];
        const rawFile = examFiles[i];
        if (!item?.marking || !rawFile) continue;

        const studentName =
          item.learnerName || item.marking?.learnerName || `Learner_${i + 1}`;
        setBulkDownloadState({
          loading: true,
          progress: `Merging script ${i + 1} of ${batchItems.length} (${studentName})…`,
          error: "",
        });

        const annotations =
          Array.isArray(item.annotations) && item.annotations.length > 0
            ? item.annotations
            : mapServerAnnotationsToClient(
                item.marking?.suggestedAnnotations || [],
              );

        const pdfBytes = await createAnnotatedPdfBytes({
          file: rawFile,
          annotations,
          marking: item.marking,
          learnerName: item.marking?.learnerName || item.marking?.learner || item.learnerName || "",
          schoolName: item.marking?.school || "",
          subjectName: item.marking?.subjectName || item.marking?.subject || (selectedSubject ? `${selectedSubject.name} (Grade ${selectedSubject.grade})` : ""),
          assessmentName: selectedAssessment
            ? `${selectedAssessment.section_name} (Term ${selection.term})`
            : "",
          pdfStampMode: "cover",
        });

        const donorPdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await masterPdf.copyPages(
          donorPdf,
          donorPdf.getPageIndices(),
        );
        copiedPages.forEach((page) => masterPdf.addPage(page));
      }

      setBulkDownloadState({
        loading: true,
        progress: "Finalizing combined PDF…",
        error: "",
      });
      const mergedBytes = await masterPdf.save();
      const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });
      saveAs(mergedBlob, `${cleanBaseName}_Combined_Marked_Scripts.pdf`);
    } catch (err) {
      setBulkDownloadState({
        loading: false,
        progress: "",
        error: err?.message || "Failed to create combined PDF.",
      });
      return;
    }
    setBulkDownloadState({ loading: false, progress: "", error: "" });
  };

  // Marks are already saved by now, so a failed copy only warns the teacher.
  const storeMarkedScript = async ({ runId, annotations, results, learnerName }) => {
    if (!runId) return "The marks are saved, but the script copy was skipped because the server did not return a marking record.";
    if (!activePaperFile) return "The marks are saved, but the script copy was skipped because the scanned paper is no longer loaded.";
    try {
      const stampedBytes = await createAnnotatedPdfBytes({
        file: activePaperFile,
        annotations,
        marking: { ...marking, results },
        learnerName,
        schoolName: marking?.school || "",
        subjectName: marking?.subjectName || marking?.subject || (selectedSubject ? `${selectedSubject.name} (Grade ${selectedSubject.grade})` : ""),
        assessmentName: selectedAssessment
          ? `${selectedAssessment.section_name} (Term ${selection.term})`
          : "",
        pdfStampMode: "cover",
      });
      const pdfBytes = await compressMarkedPdf(stampedBytes);
      const formData = new FormData();
      formData.append("run_id", String(runId));
      formData.append("script", new Blob([pdfBytes], { type: "application/pdf" }), "marked-script.pdf");
      await axios.post(`${apiUrl}/marked_scripts.php`, formData);
      return "";
    } catch (storeError) {
      const reason = storeError?.response?.data?.error || storeError?.message || "Unknown error";
      return `The marks are saved, but a copy of the script could not be stored (${reason}).`;
    }
  };

  const saveApprovedMarks = async (overrides = {}) => {
    if ((overrides.nextResults || marking?.results || []).some(hasPendingRubricEvidence)) {
      setError('Review and assign marks to every rubric criterion with unavailable evidence before saving.'); return;
    }
    if (!approved) { setError('Confirm that you have reviewed the marks before saving.'); return; }
    if (gradingSubjectMismatch(marking?.subjectName || marking?.subject, selectedSubject?.name)) {
      setError(`This script is ${marking.subjectName || marking.subject}. Select a matching subject before saving its marks.`);
      return;
    }
    const targetStudentId =
      overrides.studentId !== undefined
        ? overrides.studentId
        : selection.studentId;
    const targetAssessmentId =
      overrides.assessmentId !== undefined
        ? overrides.assessmentId
        : selection.assessmentId;

    if (!targetStudentId) {
      setError(
        "Please select or confirm the learner in the top bar before saving marks.",
      );
      setSaveStatus("");
      learnerSelectRef.current?.focus();
      return;
    }
    if (!targetAssessmentId) {
      setError("Please select an assessment section before saving marks.");
      setSaveStatus("");
      return;
    }

    const { nextAnnotations, nextResults } =
      overrides.nextResults && overrides.nextAnnotations
        ? {
            nextAnnotations: overrides.nextAnnotations,
            nextResults: overrides.nextResults,
          }
        : prepareApprovedState();

    setSavingMarks(true);
    setError("");
    setSaveStatus("Saving approved marks to learner record…");
    try {
      const savedAnnotations = nextAnnotations.filter(
        (annotation) =>
          annotation.type !== "lost" &&
          !annotation.needsPlacement &&
          Number.isFinite(Number(annotation.x)) &&
          Number.isFinite(Number(annotation.y)),
      );
      const response = await axios.post(
        `${apiUrl}/save_automated_marking.php`,
        {
          studentId: Number(targetStudentId),
          assessmentId: Number(targetAssessmentId),
          approved: true,
          results: nextResults,
          annotations: savedAnnotations,
        },
      );
      const savedSectionMark = response.data?.sectionMark;
      const savedSectionMax = response.data?.sectionMaxMark;
      const matchedStudent = learners.find(
        (l) => String(l.student_id) === String(targetStudentId),
      );
      const learnerLabel = matchedStudent
        ? `${matchedStudent.first_name} ${matchedStudent.last_name}`
        : marking?.learnerName || "learner";
      const assessmentLabel = selectedAssessment
        ? selectedAssessment.section_name
        : "assessment";
      const totalAwardedNum = Number(marking?.totalAwarded || 0);
      const totalMarksNum = Number(marking?.totalMarks || 0);
      const fmtAwarded = Number.isInteger(totalAwardedNum)
        ? totalAwardedNum
        : totalAwardedNum.toFixed(2);
      const fmtMax = Number.isInteger(totalMarksNum)
        ? totalMarksNum
        : totalMarksNum.toFixed(2);
      const scaledSuffix =
        Number.isFinite(Number(savedSectionMark)) &&
        Number.isFinite(Number(savedSectionMax)) &&
        Math.abs(Number(savedSectionMax) - totalMarksNum) > 0.01
          ? ` (recorded as ${savedSectionMark}/${savedSectionMax})`
          : "";

      setError("");
      setSaveStatus(
        `Saved ${fmtAwarded}/${fmtMax} (${percentage.toFixed(1)}%)${scaledSuffix} for ${learnerLabel} in ${assessmentLabel}.`,
      );
      setMarking((current) =>
        current ? { ...current, results: nextResults, saved: true } : current,
      );
      if (batchMode && activeBatchIndex !== null) {
        setBatchItems((current) =>
          current.map((item, idx) =>
            idx === activeBatchIndex && item.marking
              ? {
                  ...item,
                  saved: true,
                  annotations: nextAnnotations,
                  marking: {
                    ...item.marking,
                    results: nextResults,
                    saved: true,
                  },
                }
              : item,
          ),
        );
      }
      const scriptNotice = await storeMarkedScript({
        runId: response.data?.runId,
        annotations: savedAnnotations,
        results: nextResults,
        learnerName: learnerLabel,
      });
      if (scriptNotice) {
        setSaveStatus((current) => `${current} ${scriptNotice}`);
      }
    } catch (requestError) {
      setSaveStatus("");
      setError(
        requestError.response?.data?.error ||
          "Unable to save the approved marks.",
      );
    } finally {
      setSavingMarks(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${apiUrl}/fetch_dashboard.php`)
      .then(({ data }) =>
        setSubjects(Array.isArray(data?.subjects) ? data.subjects : []),
      )
      .catch(() =>
        setError(
          "Unable to load subjects. Please check that the PHP API is running.",
        ),
      );
  }, [apiUrl]);

  useEffect(() => {
    if (!selection.subjectId) {
      setLearners([]);
      setAssessments([]);
      return;
    }
    setSelectionLoading(true);
    Promise.all([
      axios.get(
        `${apiUrl}/fetch_class_marks.php?subject_id=${selection.subjectId}&term=${selection.term}`,
      ),
      axios.get(
        `${apiUrl}/task_sections.php?subject_id=${selection.subjectId}&term=${selection.term}`,
      ),
    ])
      .then(([classResponse, sectionsResponse]) => {
        setLearners(
          Array.isArray(classResponse.data?.students)
            ? classResponse.data.students
            : [],
        );
        setAssessments(
          Array.isArray(sectionsResponse.data?.sections)
            ? sectionsResponse.data.sections
            : [],
        );
      })
      .catch(() => setError("Unable to load learners and assessment sections."))
      .finally(() => setSelectionLoading(false));
  }, [apiUrl, selection.subjectId, selection.term]);

  const updateResult = (index, field, value) => {
    setApproved(false);
    if (field === 'rubricCriterion') {
      const amend = current => {
        if (!current) return current;
        const results = current.results.map((result, resultIndex) => resultIndex === index ? reviewRubricCriterion(result, value.criterionIndex, value.score) : result);
        return { ...current, saved: false, results, ...calculateMarkingTotals(results) };
      };
      setSaveStatus(''); setMarking(amend);
      if (batchMode && activeBatchIndex !== null) setBatchItems(current => current.map((item, itemIndex) => itemIndex === activeBatchIndex
        ? { ...item, marking: amend(item.marking) } : item));
      return;
    }
    let updatedQuestionKey = "";
    let updatedAwardedTicks = null;

    setSaveStatus("");
    setMarking((current) => {
      if (!current) return current;
      const results = current.results.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const next = { ...item, [field]: value };
        if (field === "marksAwarded") {
          next.marksAwarded = Math.min(
            Math.max(0, Number(value) || 0),
            Number(next.maxMarks) || 0,
          );
          updatedQuestionKey = normalizeQuestionKeyClient(next.questionNumber);
          updatedAwardedTicks = Math.ceil(next.marksAwarded);
        } else if (field === "maxMarks") {
          const nextMax = Math.max(0.5, Number(value) || 0);
          next.maxMarks = nextMax;
          next.marksAwarded = Math.min(
            Number(next.marksAwarded || 0),
            nextMax,
          );
        }
        return next;
      });
      return {
        ...current,
        saved: false,
        results,
        ...calculateMarkingTotals(results),
      };
    });

    if (field === "marksAwarded") {
      const targetItem = marking?.results?.[index];
      const targetKey =
        updatedQuestionKey ||
        normalizeQuestionKeyClient(targetItem?.questionNumber);
      const clampedMarks = Math.min(
        Math.max(0, Number(value) || 0),
        Number(targetItem?.maxMarks) || 0,
      );
      const awardedCount =
        updatedAwardedTicks !== null
          ? updatedAwardedTicks
          : Math.ceil(clampedMarks);
      if (targetKey) {
        setPaperAnnotations((current) =>
          current.map((annotation) => {
            if (
              normalizeQuestionKeyClient(annotation.questionNumber) !==
              targetKey
            ) {
              return annotation;
            }
            const markNum = Number(annotation.markNumber || 1);
            const nowAwarded = markNum <= awardedCount;
            return {
              ...annotation,
              awarded: nowAwarded,
              status: nowAwarded ? "awarded" : "not_awarded",
              type: nowAwarded
                ? annotation.type === "confirmed"
                  ? "confirmed"
                  : "suggested"
                : "lost",
            };
          }),
        );
      }
    }

    if (batchMode) {
      if (field === "maxMarks") {
        const targetQNum = marking?.results?.[index]?.questionNumber;
        const newMax = Math.max(0.5, Number(value) || 0);
        setBatchItems((current) =>
          current.map((item) => {
            if (!item?.marking?.results) return item;
            const updatedResults = item.marking.results.map((r) =>
              r.questionNumber === targetQNum
                ? {
                    ...r,
                    maxMarks: newMax,
                    marksAwarded: Math.min(Number(r.marksAwarded || 0), newMax),
                  }
                : r,
            );
            return {
              ...item,
              marking: {
                ...item.marking,
                results: updatedResults,
                ...calculateMarkingTotals(updatedResults),
              },
            };
          }),
        );
      } else if (activeBatchIndex !== null) {
        setBatchItems((current) =>
          current.map((item, itemIndex) => {
            if (itemIndex !== activeBatchIndex || !item.marking) return item;
            const results = item.marking.results.map((result, itemResultIndex) =>
              itemResultIndex === index
                ? {
                    ...result,
                    [field]: field === "marksAwarded"
                      ? Math.min(Math.max(0, Number(value) || 0), Number(result.maxMarks) || 0)
                      : value,
                  }
                : result,
            );
            return {
              ...item,
              marking: { ...item.marking, results, ...calculateMarkingTotals(results) },
            };
          }),
        );
      }
    }
  };

  const submitMarking = async (event) => {
    event.preventDefault();
    if (!recovery.ready || loading || recovery.controller?.activeRun) return;
    if (batchMode) {
      if (
        !memoFile ||
        examFiles.length === 0 ||
        !selection.subjectId ||
        !selection.assessmentId
      ) {
        setError(
          "Select the subject and assessment, then upload the memo and at least one learner script.",
        );
        return;
      }
      const context = { subjectId: selection.subjectId, assessmentId: selection.assessmentId, term: selection.term,
        memo: getMemoCacheKey(memoFile), files: examFiles.map(getMarkingDocumentId), assessmentType };
      const resume = JSON.stringify(processingContextRef.current) === JSON.stringify(context);
      const completedItems = examFiles.map((file, index) => resume && batchItems[index]
        ? { ...batchItems[index], sourceIndex: index }
        : { fileName: file.name, sourceIndex: index, status: 'pending', learnerName: 'Waiting to mark', studentId: '' });
      let completedCount = completedItems.filter(item => item.marking || item.status === 'skipped').length;
      // Check the plan allowance up front so a batch does not stop half-way unexpectedly.
      // Re-marks of the same script are free on the server, so only warn rather than block.
      const toMark = examFiles.length - completedCount;
      const latestAllowance = (await refreshAllowance()) || allowance;
      if (latestAllowance && !latestAllowance.unlimited && latestAllowance.remaining < toMark) {
        if (latestAllowance.remaining <= 0) {
          setError("You have used all the scripts in your plan. Buy a top-up above or upgrade your plan to keep marking.");
          return;
        }
        if (!window.confirm(`This batch has ${toMark} scripts but your plan has ${latestAllowance.remaining} left. Mark the first ${latestAllowance.remaining} now? You can buy a top-up to mark the rest.`)) return;
      }
      let outOfScripts = false;
      const run = recovery.controller?.beginRun({ ...draftSnapshot, processingContext: context,
        batchItems: completedItems, batchProgress: { current: completedCount, total: examFiles.length } }) ||
        (!recovery.controller ? { abort: new AbortController() } : null);
      if (!run) return;
      processingContextRef.current = context;
      const request = { owner: recovery.controller?.owner, signal: run.abort.signal };
      setLoading(true);
      setError("");
      setBatchItems(completedItems);
      setBatchProgress({ current: completedCount, total: examFiles.length });
      setLoadingState({
        percent: 8,
        stepIndex: 1,
        stageLabel: "Aligning answer memo & rubric criteria…",
        elapsedSeconds: 0,
      });
      try {
        await recovery.controller?.flush();
        const preparedMemo = await ensureMemoProfile(memoFile);
        if (!preparedMemo) throw new Error("The memorandum could not be prepared. Check its questions and totals before retrying.");
        assertMemoReady(preparedMemo);
        setLoadingState((curr) => ({
          ...curr,
          percent: Math.max(curr.percent, 25),
          stepIndex: 2,
          stageLabel: "Reading learner name and cover details…",
        }));
        let nextIndex = 0;
        const concurrency = Math.min(6, examFiles.length);

        const processOneBatchIndex = async (index) => {
          const file = examFiles[index];
          try {
            const markingData = await postProcessMarking(
              apiUrl,
              async () => {
                const fd = new FormData();
                fd.append('assessment_type', assessmentType);
                fd.append('subject_name', selectedSubject?.name || selectedSubject?.subject_name || '');
                await appendLearnerDocumentToFormData(fd, file);
                if (preparedMemo) {
                  fd.append("memo_profile", JSON.stringify(preparedMemo));
                }
                return fd;
              }, request,
            );
            if (markingData.skipped) {
              completedItems[index] = { fileName: file.name, status: "skipped", learnerName: "", reason: markingData.reason };
              return;
            }
            const identity = markingData.learnerName || "";
            const number = markingData.learnerStudentNumber || "";
            completedItems[index] = { fileName: file.name, sourceIndex: index, marking: markingData,
              annotations: mapServerAnnotationsToClient(markingData.suggestedAnnotations), learnerName: identity,
              studentId: '', status: 'needs_confirmation' };
            await recovery.controller?.patchRun(run, state => {
              const items = [...(state.batchItems || completedItems)]; items[index] = completedItems[index];
              return { batchItems: items };
            });
            const pageText = await optionalPdfResult(extractPdfFirstPageText(file), '');
            const matchedLearner = matchLearnerFromIdentity(learners, {
              learnerName: identity,
              learnerStudentNumber: number,
              fileName: file.name,
              pageText,
            });
            const initialAnnotations = mapServerAnnotationsToClient(markingData.suggestedAnnotations);
            const snappedAnnotations = await optionalPdfResult(snapAnnotationsToPdfText(
              file,
              initialAnnotations,
            ), initialAnnotations);
            completedItems[index] = {
              fileName: file.name,
              sourceIndex: index,
              marking: markingData,
              annotations: snappedAnnotations,
              studentId: matchedLearner ? String(matchedLearner.student_id) : "",
              learnerName:
                identity ||
                (matchedLearner
                  ? `${matchedLearner.first_name} ${matchedLearner.last_name}`
                  : ""),
              status: matchedLearner ? "matched" : "needs_confirmation",
            };
          } catch (requestError) {
            const noScriptsLeft = requestError.response?.status === 402;
            if (noScriptsLeft) outOfScripts = true;
            completedItems[index] = {
              fileName: file.name,
              learnerName: noScriptsLeft ? "Not marked: no scripts left" : "Could not process",
              status: "failed",
              error: requestError.response?.data?.error || requestError.message,
            };
          } finally {
            completedCount += 1;
            if (completedItems[index]) completedItems[index].sourceIndex = index;
            setBatchItems([...completedItems]);
            setBatchProgress({
              current: completedCount,
              total: examFiles.length,
            });
            await recovery.controller?.patchRun(run, state => {
              const items = [...(state.batchItems || completedItems)]; items[index] = completedItems[index];
              return { batchItems: items, batchProgress: { current: completedCount, total: examFiles.length } };
            });
          }
        };

        const workers = Array.from({ length: concurrency }, async () => {
          while (nextIndex < examFiles.length) {
            if (outOfScripts || run.abort.signal.aborted || (request.owner && getMarkingDraftOwner(apiUrl) !== request.owner)) break;
            const currentIdx = nextIndex;
            nextIndex += 1;
            if (completedItems[currentIdx]?.marking || completedItems[currentIdx]?.status === 'skipped') continue;
            await processOneBatchIndex(currentIdx);
          }
        });
        await Promise.all(workers);
        refreshAllowance();
        if (outOfScripts) {
          setError("Your plan ran out of scripts during this batch. The marked scripts are safe; buy a top-up above, then resume to mark the rest.");
        }

        const finalItems = completedItems.filter(Boolean);
        setBatchItems(finalItems);
        setLoadingState((curr) => ({
          ...curr,
          percent: 100,
          stepIndex: 3,
          stageLabel: "Completed!",
        }));
        const firstMarked = finalItems.findIndex((it) => it.marking);
        if (firstMarked >= 0) {
          const item = finalItems[firstMarked];
          setMarking(item.marking);
          setActiveBatchIndex(firstMarked);
          setPaperFile(examFiles[firstMarked] || null);
          setPaperAnnotations(item.annotations || []);
          setApproved(false);
          setSelection((current) => ({ ...current, studentId: item.studentId || "" }));
          setSetupCollapsed(true);
          setActiveQuestionIndex(firstToReview(item.marking));
        } else if (finalItems.length > 0) {
          setError(
            finalItems[0]?.error || finalItems[0]?.reason || "Unable to process the marking request.",
          );
        }
      } catch (requestError) {
        if (completedCount === 0) {
          setBatchItems([]);
          await recovery.controller?.patchRun(run, { batchItems: [], setupCollapsed: false });
        }
        setError(requestError.response?.data?.error || requestError.message || 'Unable to prepare this batch for marking.');
      } finally {
        const items = recovery.controller?.state?.batchItems || completedItems;
        const first = items.findIndex(item => item?.marking);
        await recovery.controller?.finishRun(run, {
          interrupted: items.length < examFiles.length || items.some(item => !item?.marking && item?.status !== 'skipped'),
          ...(first >= 0 && !recovery.controller?.state?.marking ? {
            marking: items[first].marking, paperFile: examFiles[first], paperAnnotations: items[first].annotations || [],
            activeBatchIndex: first, setupCollapsed: true,
            selection: { ...(recovery.controller?.state?.selection || selection), studentId: items[first].studentId || '' },
          } : {}),
        });
        setLoading(false);
      }
      return;
    }
    if (
      !examFile ||
      !memoFile ||
      !selection.subjectId ||
      !selection.assessmentId
    ) {
      setError(
        "Select the subject and assessment section, and upload both documents before continuing.",
      );
      return;
    }

    const run = recovery.controller?.beginRun({ ...draftSnapshot, marking: null, paperFile: examFile,
      paperAnnotations: [], activeBatchIndex: null, batchProgress: { current: 0, total: 1 } }) ||
      (!recovery.controller ? { abort: new AbortController() } : null);
    if (!run) return;
    const request = { owner: recovery.controller?.owner, signal: run.abort.signal };
    setLoading(true);
    setError("");
    setMarking(null);
    setPaperFile(examFile);
    setPaperAnnotations([]);
    setActiveBatchIndex(null);
    setApproved(false);
    setBatchProgress({ current: 1, total: 1 });
    setLoadingState({
      percent: 6,
      stepIndex: 0,
      stageLabel: "Preparing memo or writing rubric…",
      elapsedSeconds: 0,
    });

    try {
      await recovery.controller?.flush();
      const preparedSingleMemo = await ensureMemoProfile(memoFile);
      if (!preparedSingleMemo) throw new Error("The memorandum could not be prepared. Check its questions and totals before retrying.");
      assertMemoReady(preparedSingleMemo);
      const markingData = await postProcessMarking(
        apiUrl,
        async () => {
          const fd = new FormData();
          fd.append('assessment_type', assessmentType);
          fd.append('subject_name', selectedSubject?.name || selectedSubject?.subject_name || '');
          await appendLearnerDocumentToFormData(fd, examFile);
          setLoadingState((curr) => ({
            ...curr,
            percent: Math.max(curr.percent, 32),
            stepIndex: 2,
            stageLabel: "Reading learner name and cover details…",
          }));
          if (preparedSingleMemo) {
            fd.append("memo_profile", JSON.stringify(preparedSingleMemo));
          }
          return fd;
        }, request,
      );
      if (markingData.skipped) throw new Error(markingData.reason);
      const initialAnnotations = mapServerAnnotationsToClient(markingData.suggestedAnnotations);
      await recovery.controller?.patchRun(run, { marking: markingData, paperFile: examFile,
        paperAnnotations: initialAnnotations, setupCollapsed: true, activeQuestionIndex: 0 });
      setLoadingState((curr) => ({
        ...curr,
        percent: Math.max(curr.percent, 90),
        stepIndex: 3,
        stageLabel: "Placing paper ticks & matching learner…",
      }));
      const pageText = await optionalPdfResult(extractPdfFirstPageText(examFile), '');
      const matchedLearner = matchLearnerFromIdentity(learners, {
        learnerName: markingData.learnerName || "",
        learnerStudentNumber: markingData.learnerStudentNumber || "",
        fileName: examFile.name,
        pageText,
      });
      if (matchedLearner) {
        setSelection((current) =>
          current.studentId
            ? current
            : { ...current, studentId: String(matchedLearner.student_id) },
        );
      }
      setMarking(markingData);
      setPaperFile(examFile);
      const snappedAnnotations = await optionalPdfResult(snapAnnotationsToPdfText(
          examFile,
          initialAnnotations,
        ), initialAnnotations);
      setPaperAnnotations(snappedAnnotations);
      await recovery.controller?.patchRun(run, state => ({ paperAnnotations: snappedAnnotations,
        batchProgress: { current: 1, total: 1 },
        selection: { ...state.selection, studentId: state.selection?.studentId || (matchedLearner ? String(matchedLearner.student_id) : '') },
      }));
      setLoadingState((curr) => ({ ...curr, percent: 100 }));
      setActiveBatchIndex(null);
      setSetupCollapsed(true);
      setActiveQuestionIndex(firstToReview(markingData));
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Unable to process the marking request.",
      );
    } finally {
      await recovery.controller?.finishRun(run, { interrupted: !recovery.controller?.state?.marking });
      setLoading(false);
    }
  };

  const clearSavedMarking = async () => {
    if (loading || recovery.controller?.activeRun || !window.confirm('Clear the saved scripts, marking edits and annotations from this browser and start a new marking session?')) return;
    try {
      await recovery.clear();
      memoCacheRef.current = { key: '', promise: null, data: null }; processingContextRef.current = null;
      applyRecovery({}, false); setError(''); setSaveStatus('');
    } catch (cacheError) { setError(cacheError.message || 'Could not clear the saved marking.'); }
  };

  // Setup is three steps; each is "done" once its required inputs are filled in.
  const scriptsToMark = batchMode ? examFiles.length : examFile ? 1 : 0;
  const setupStepDone = (step) =>
    step === 1 ? Boolean(selection.subjectId && selection.assessmentId)
      : step === 2 ? Boolean(scriptsToMark && memoFile)
        : false;
  const currentSetupStep = !setupStepDone(1) ? 1 : !setupStepDone(2) ? 2 : 3;
  const setupStepState = (step) =>
    setupStepDone(step) ? "is-done" : step === currentSetupStep ? "is-current" : "is-upcoming";

  const renderSetupForm = () => (
    <form className="marking-upload-card marking-steps-form" onSubmit={submitMarking}>
      <section id="marking-step-1" className={`marking-step-card ${setupStepState(1)}`}>
        <header className="marking-step-head">
          <span className="marking-step-badge" aria-hidden="true">{setupStepDone(1) ? "✓" : "1"}</span>
          <div>
            <h2>
              What are you marking?
              <HelpTip label="About choosing the assessment">
                The approved marks are saved into this assessment in your mark book.
                Create the assessment under Subjects first if it isn&apos;t in the list.
              </HelpTip>
            </h2>
            <p>Choose the class and the assessment these marks belong to.</p>
          </div>
        </header>
        <div className="marking-selection-grid">
          <label>
            Subject
            <select
              value={selection.subjectId}
              onChange={(event) =>
                setSelection({
                  subjectId: event.target.value,
                  term: selection.term,
                  studentId: "",
                  assessmentId: "",
                })
              }
            >
              <option value="">Select subject</option>
              {subjects.map((subject) => (
                <option
                  key={subject.subject_id || subject.id}
                  value={subject.subject_id || subject.id}
                >
                  {subject.name} (Grade {subject.grade})
                </option>
              ))}
            </select>
          </label>
          <label>
            Term
            <select
              value={selection.term}
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  term: event.target.value,
                  studentId: "",
                  assessmentId: "",
                }))
              }
            >
              {[1, 2, 3, 4].map((term) => (
                <option key={term} value={term}>
                  Term {term}
                </option>
              ))}
            </select>
          </label>
          {!batchMode && (
            <label>
              Learner (auto-detected if left blank)
              <select
                value={selection.studentId}
                disabled={!selection.subjectId || selectionLoading}
                onChange={(event) =>
                  setSelection((current) => ({
                    ...current,
                    studentId: event.target.value,
                  }))
                }
              >
                <option value="">Auto-detect from script</option>
                {learners.map((learner) => (
                  <option key={learner.student_id} value={learner.student_id}>
                    {learner.first_name} {learner.last_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Assessment section
            <select
              value={selection.assessmentId}
              disabled={!selection.subjectId || selectionLoading}
              onChange={(event) =>
                setSelection((current) => ({
                  ...current,
                  assessmentId: event.target.value,
                }))
              }
            >
              <option value="">Select assessment</option>
              {assessments.map((assessment) => (
                <option key={assessment.id} value={assessment.id}>
                  {assessment.section_name} (/{assessment.max_mark})
                </option>
              ))}
            </select>
          </label>
        </div>
        {selectionLoading && (
          <p className="marking-progress">
            Loading learners and assessment sections…
          </p>
        )}
        <details className="marking-advanced">
          <summary>Advanced: assessment type</summary>
          <label className="marking-assessment-type">
            Assessment type
            <select aria-label="Assessment type" value={assessmentType} disabled={loading} onChange={event => { setAssessmentType(event.target.value); setMemoPreparation(null); setMemoAssessmentConfirmation(''); setError(''); }}>
              {/* "auto" marks questions and answers and still recognises essays and summaries in the memo. */}
              <option value="auto">Questions and answers</option>
              <option value="transactional">Transactional writing</option>
              <option value="essay">Essay</option>
            </select>
            <small>For essays and transactional texts, upload the task instructions and full scoring rubric as the memo. Writing can span pages and does not need printed question numbers.</small>
          </label>
        </details>
      </section>

      <section id="marking-step-2" className={`marking-step-card ${setupStepState(2)}`}>
        <header className="marking-step-head">
          <span className="marking-step-badge" aria-hidden="true">{setupStepDone(2) ? "✓" : "2"}</span>
          <div>
            <h2>
              Upload the scripts and memo
              <HelpTip label="About uploading scripts and the memo">
                Add one file per learner, or one PDF of the whole class and MarkSure splits it
                by learner. Use the memo for this exact paper; for essays and letters, upload
                the task and your rubric instead.
              </HelpTip>
            </h2>
            <p>Clear, upright scans give the most accurate marks.</p>
          </div>
          <button
            type="button"
            className="btn-toggle-scan-guide"
            onClick={() => setShowScanGuide((prev) => !prev)}
            aria-expanded={showScanGuide}
          >
            {showScanGuide ? "Hide scanning guide" : "How do I scan?"}
          </button>
        </header>
        <div
          className="marking-mode-switch"
          role="group"
          aria-label="Marking mode"
        >
          <button
            type="button"
            className={batchMode ? "selected" : ""}
            onClick={() => {
              setBatchMode(true);
              if (examFiles.length > 0) {
                setPaperFile(examFiles[activeBatchIndex ?? 0] || examFiles[0]);
              }
            }}
          >
            Whole class
          </button>
          <button
            type="button"
            className={!batchMode ? "selected" : ""}
            onClick={() => {
              setBatchMode(false);
              setSinglePdfInfo(null);
              if (examFile) {
                setPaperFile(examFile);
              }
            }}
          >
            One learner
          </button>
        </div>
        {showScanGuide && (
          <div className="teacher-scan-guide-card">
            <div className="scan-guide-nav" role="tablist">
              <button
                type="button"
                className={`scan-guide-tab ${scanGuideTab === "copier" ? "is-active" : ""}`}
                onClick={() => setScanGuideTab("copier")}
                role="tab"
                aria-selected={scanGuideTab === "copier"}
              >
                School Photocopier (Whole Class)
              </button>
              <button
                type="button"
                className={`scan-guide-tab ${scanGuideTab === "phone" ? "is-active" : ""}`}
                onClick={() => setScanGuideTab("phone")}
                role="tab"
                aria-selected={scanGuideTab === "phone"}
              >
                Phone Camera (Single / Memo)
              </button>
              <button
                type="button"
                className={`scan-guide-tab ${scanGuideTab === "tips" ? "is-active" : ""}`}
                onClick={() => setScanGuideTab("tips")}
                role="tab"
                aria-selected={scanGuideTab === "tips"}
              >
                3 Golden Rules for Accuracy
              </button>
            </div>

            {scanGuideTab === "copier" && (
              <div className="scan-guide-panel" role="tabpanel">
                <h4>
                  Fastest method: Scan your whole class stack in 2 minutes
                </h4>
                <ol className="scan-steps-list">
                  <li>
                    <strong>Remove all staples:</strong> Pull out staples and
                    make sure all tests are facing upright and in the correct
                    order.
                  </li>
                  <li>
                    <strong>Place stack in the top feeder:</strong> Put the
                    stack into the automatic document feeder on top of the
                    school printer (Ricoh, Canon, Bizhub, Kyocera, HP, etc.).
                  </li>
                  <li>
                    <strong>Select "Scan to Email":</strong> On the printer
                    touchscreen, choose <em>Scanner → Scan to Email</em> and
                    type your own email address.
                  </li>
                  <li>
                    <strong>Press Start:</strong> Press the big green button.
                    The machine will feed through 30+ papers into a single PDF
                    in about 60 seconds.
                  </li>
                  <li>
                    <strong>Upload & Split in MarkSure:</strong> Open your email
                    on your computer, save the attached PDF, and upload it
                    below. MarkSure will automatically split it into individual
                    student tests for you!
                  </li>
                </ol>
                <div className="scan-callout-tip">
                  <strong>Pro Tip:</strong> If your school machine has a USB
                  port on the front, you can choose "Scan to USB" to save
                  directly onto your flash drive without needing internet at the
                  printer!
                </div>
              </div>
            )}

            {scanGuideTab === "phone" && (
              <div className="scan-guide-panel" role="tabpanel">
                <h4>
                  Scanning with your smartphone (Best for Memos & single tests)
                </h4>
                <ol className="scan-steps-list">
                  <li>
                    <strong>
                      Use a free Scanner App (Not standard camera):
                    </strong>{" "}
                    Regular photos cast hand shadows and distort angles. Free
                    scanner apps automatically detect corners, straighten the
                    page, and whiten the background:
                    <ul>
                      <li>
                        <strong>Android:</strong> Open <em>Google Drive</em> app
                        → tap the <strong>+</strong> (Plus) icon → tap{" "}
                        <strong>Scan</strong> (camera icon).
                      </li>
                      <li>
                        <strong>iPhone:</strong> Open the built-in{" "}
                        <em>Notes</em> app → tap the Camera icon → tap{" "}
                        <strong>Scan Documents</strong>.
                      </li>
                      <li>
                        <strong>Or download:</strong> <em>Microsoft Lens</em>{" "}
                        (free on both Android and iPhone).
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Lay paper flat:</strong> Place the test flat on a
                    desk under good, even room lighting. Avoid casting a shadow
                    over the writing with your phone.
                  </li>
                  <li>
                    <strong>Hold steady directly above:</strong> Hold the camera
                    directly over the paper (not tilted). The app will snap the
                    page automatically.
                  </li>
                  <li>
                    <strong>Send to your computer:</strong> Save as PDF and
                    email it to yourself or send it via WhatsApp Web, then
                    upload here.
                  </li>
                </ol>
              </div>
            )}

            {scanGuideTab === "tips" && (
              <div className="scan-guide-panel" role="tabpanel">
                <h4>3 Golden Rules for 100% AI Accuracy</h4>
                <ul className="scan-steps-list">
                  <li>
                    <strong>Keep it upright:</strong> Make sure pages are not
                    sideways or upside-down so the AI can read questions and
                    words correctly.
                  </li>
                  <li>
                    <strong>Don't cut off margins:</strong> Ensure the student's
                    name at the top and question numbers down the left margin
                    are fully visible.
                  </li>
                  <li>
                    <strong>Dark enough handwriting:</strong> If students wrote
                    in pencil, set the photocopier density to "Text / Dark" so
                    faint marks are clearly visible.
                  </li>
                </ul>
                <div className="scan-callout-warning">
                  <strong>Avoid:</strong> Creased or folded pages over answers,
                  or scanning in dark rooms with harsh hand shadows across the
                  handwriting.
                </div>
              </div>
            )}
          </div>
        )}
        <div className="marking-upload-grid">
          {!batchMode && (
            <FileDropzone
              label="Learner exam paper"
              inputId="learner-exam"
              file={examFile}
              onFileChange={(file, message) => {
                setExamFile(file);
                if (file) setPaperFile(file);
                setFieldErrors((current) => ({ ...current, exam: message }));
              }}
              onHelpClick={() => {
                setShowScanGuide(true);
                setScanGuideTab("phone");
              }}
            />
          )}
          {batchMode && (
            <div className="marking-upload-field">
              <div className="marking-label-with-hint">
                <label htmlFor="learner-exams">Learner exam papers</label>
                <button
                  type="button"
                  className="btn-inline-scan-help"
                  onClick={() => {
                    setShowScanGuide(true);
                    setScanGuideTab("copier");
                  }}
                  title="Click for instructions on scanning a class batch"
                >
                  How to scan papers
                </button>
              </div>
              <div
                className={`marking-dropzone${examFiles.length ? " has-file" : ""}`}
              >
                <input
                  id="learner-exams"
                  type="file"
                  accept={ACCEPTED_TYPES}
                  multiple
                  onChange={handleBatchFilesSelect}
                />
                <strong>
                  {examFiles.length
                    ? `${examFiles.length} learner script${examFiles.length === 1 ? "" : "s"} selected`
                    : "Choose all learner scripts"}
                </strong>
                <span>
                  Select multiple PDF, JPG, PNG, or WebP files (or 1 combined PDF
                  to split)
                </span>
              </div>
              {singlePdfInfo && (
                <div
                  className="combined-pdf-splitter-banner"
                  role="region"
                  aria-label="Combined batch PDF splitter"
                >
                  <div className="splitter-banner-title">
                    <div>
                      <strong>
                        Single combined batch PDF detected (
                        {singlePdfInfo.pageCount} pages)
                      </strong>
                      <p>
                        If you scanned all students together into this single
                        file, choose how many pages each student's test has:
                      </p>
                    </div>
                  </div>
                  <div className="splitter-banner-actions">
                    <label className="splitter-pages-label">
                      Pages per student test:
                      <input
                        type="number"
                        min="1"
                        max={singlePdfInfo.pageCount}
                        value={singlePdfInfo.pagesPerStudent}
                        onChange={(e) => {
                          const val = Math.max(
                            1,
                            Math.min(
                              singlePdfInfo.pageCount,
                              parseInt(e.target.value, 10) || 1,
                            ),
                          );
                          setSinglePdfInfo((prev) =>
                            prev ? { ...prev, pagesPerStudent: val } : null,
                          );
                        }}
                        className="splitter-number-input"
                      />
                    </label>
                    <span className="splitter-output-badge">
                      ={" "}
                      <strong>
                        {Math.ceil(
                          singlePdfInfo.pageCount / singlePdfInfo.pagesPerStudent,
                        )}
                      </strong>{" "}
                      student scripts
                    </span>
                    <button
                      type="button"
                      className="btn-split-batch-pdf"
                      disabled={singlePdfInfo.isSplitting}
                      onClick={handleSplitCombinedPdf}
                    >
                      {singlePdfInfo.isSplitting
                        ? "Splitting PDF…"
                        : `Split into ${Math.ceil(singlePdfInfo.pageCount / singlePdfInfo.pagesPerStudent)} student scripts`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <FileDropzone
            label={['transactional', 'essay'].includes(assessmentType) ? 'Writing task and rubric' : 'Answer memo'}
            inputId="answer-memo"
            file={memoFile}
            disabled={loading}
            onFileChange={(file, message) => {
              if (loading || recovery.controller?.activeRun) return;
              memoCacheRef.current = { key: '', promise: null, data: null };
              setMemoPreparation(null); setMemoAssessmentConfirmation(''); setError('');
              setMemoFile(file);
              setFieldErrors((current) => ({ ...current, memo: message }));
            }}
            onHelpClick={() => {
              setShowScanGuide(true);
              setScanGuideTab("phone");
            }}
          />
        </div>
        {(fieldErrors.exam || fieldErrors.memo) && (
          <p className="marking-error">{fieldErrors.exam || fieldErrors.memo}</p>
        )}
      </section>

      <section id="marking-step-3" className={`marking-step-card ${setupStepState(3)}`}>
        <header className="marking-step-head">
          <span className="marking-step-badge" aria-hidden="true">{setupStepDone(3) ? "✓" : "3"}</span>
          <div>
            <h2>
              Check and start
              <HelpTip label="About starting the marking">
                Several scripts are marked at the same time. Your progress is saved in this
                browser, so you can come back to the results if the page closes.
              </HelpTip>
            </h2>
            <p>MarkSure suggests marks. You review and approve every one before it is saved.</p>
          </div>
        </header>
        <dl className="marking-summary">
          <div>
            <dt>Marking</dt>
            <dd>
              {selectedSubject && selectedAssessment
                ? `${selectedSubject.name} (Grade ${selectedSubject.grade}) · Term ${selection.term} · ${selectedAssessment.section_name} (/${selectedAssessment.max_mark})`
                : "Choose the subject and assessment in step 1"}
            </dd>
          </div>
          <div>
            <dt>Learner scripts</dt>
            <dd>{scriptsToMark ? `${scriptsToMark} script${scriptsToMark === 1 ? "" : "s"}` : "None uploaded yet"}</dd>
          </div>
          <div>
            <dt>{['transactional', 'essay'].includes(assessmentType) ? "Task and rubric" : "Memo"}</dt>
            <dd>{memoFile ? memoFile.name : "Not uploaded yet"}</dd>
          </div>
          {allowance && !allowance.unlimited && scriptsToMark > 0 && (
            <div className={scriptsToMark > allowance.remaining ? "is-warning" : ""}>
              <dt>Your plan</dt>
              <dd>
                {scriptsToMark > allowance.remaining
                  ? `Only ${allowance.remaining} script${allowance.remaining === 1 ? "" : "s"} left. Buy a top-up above to mark them all.`
                  : `Uses ${scriptsToMark} of your ${allowance.remaining} remaining scripts`}
              </dd>
            </div>
          )}
        </dl>
        <MemoPreparationReview fileName={memoFile?.name}
          preparation={memoPreparation?.key === getMemoCacheKey(memoFile) ? memoPreparation : null}
          selectedMaximum={selectedAssessment?.max_mark} busy={loading}
          selectionConfirmed={memoAssessmentConfirmation === memoAssessmentKey(memoPreparation?.data)}
          onConfirmSelection={confirmed => { setMemoAssessmentConfirmation(confirmed ? memoAssessmentKey(memoPreparation?.data) : ''); setError(''); }}
          onConfirmTotal={confirmMemoTotal}
          onRetry={retryMemoPreparation} />
        {error && error !== memoPreparation?.review?.message && (
          <p className="marking-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="marking-submit"
          type="submit"
          disabled={loading || selectionLoading || !recovery.ready}
        >
          {loading
            ? `Marking ${batchProgress.current} of ${batchProgress.total}…`
            : batchMode
              ? "Start class batch marking"
              : "Start automated marking"}
        </button>
        {loading && (
          <div className="form-ai-loader-container">
            <AiGeneratingLoader />
            <p className="marking-progress">
              This can take a few minutes for multi-page scans. Please keep this
              window open.
            </p>
          </div>
        )}
      </section>
    </form>
  );

  return (
    <main
      className={`automated-marking-page ${isStudioActive ? "has-active-marking" : ""}`}
    >
      {/* Only show recovery when there is something to report, not on every visit. */}
      {recovery.controller && (!recovery.ready || recovery.recovered || recovery.interrupted || recovery.status === 'error' || recovery.status === 'saving' || marking) && <section className={`marking-recovery-banner ${recovery.status === 'error' ? 'is-error' : ''} ${marking && !recovery.interrupted && recovery.status !== 'error' ? 'is-compact' : ''}`} aria-label="Marking recovery">
        <div><strong>{!recovery.ready ? 'Recovering saved marking…' : recovery.status === 'error' ? 'Browser recovery is unavailable' : recovery.status === 'saving' ? 'Saving marking…' : recovery.recovered ? 'Your marking has been recovered' : 'Automatic marking recovery'}</strong>
          <p>{recovery.error || (recovery.interrupted
            ? 'Completed scripts and edits are safe. Resume to mark only the remaining or failed scripts.'
            : 'Scripts, review edits and annotations are saved in this browser for your account.')}</p>
          {recovery.savedAt && recovery.status !== 'error' && <small>Last saved {new Date(recovery.savedAt).toLocaleString()}</small>}
        </div>
        <div className="marking-recovery-actions">
          {recovery.interrupted && !loading && <button type="button" onClick={() => submitMarking({ preventDefault() {} })} disabled={!recovery.ready || selectionLoading}>Resume remaining marking</button>}
          <button type="button" onClick={clearSavedMarking} disabled={loading || !recovery.ready}>Start new marking</button>
        </div>
      </section>}
      {!isStudioActive && (
        <>
          <header className="automated-marking-header">
            <div className="automated-marking-title-row">
              <h1>Automated marking</h1>
              <TourButton onClick={setupTour.start} />
            </div>
            <p>
              Upload a class set and its memo. MarkSure suggests the marks, and
              you check and approve them.
            </p>
            <details className="marking-about">
              <summary>How AI marking works</summary>
              <p>
                AI marks are recommendations. Review every question and resolve
                all flagged items before saving official marks.
              </p>
              <p>
                <strong>Privacy:</strong> scripts are used only for this marking
                request, temporary uploads are deleted after processing, and
                saved results remain inside your authenticated MarkSure school
                account.
              </p>
            </details>
          </header>
          <MarkingAllowance apiUrl={apiUrl} allowance={allowance} />
          <ol className="marking-steps" aria-label="Automated marking steps">
            {["Choose the assessment", "Upload scripts & memo", "Check & start"].map((label, index) => (
              <li key={label} className={setupStepState(index + 1)}>
                <a href={`#marking-step-${index + 1}`}>
                  <strong>{setupStepDone(index + 1) ? "✓" : index + 1}</strong>
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ol>
          {renderSetupForm()}
        </>
      )}

      {isStudioActive && (
        <>
          {marking ? (
            <header className="marking-studio-header">
              <div className="studio-header-left">
                {batchMode && batchItems.length > 0 && (
                  <div className="studio-batch-nav">
                    <button
                      type="button"
                      className="btn-studio-back"
                      onClick={() => {
                        setMarking(null);
                        setSetupCollapsed(false);
                      }}
                      title="Return to the batch class list"
                    >
                      ← Class batch
                    </button>
                    <button
                      type="button"
                      className="btn-studio-arrow"
                      disabled={activeBatchIndex === 0}
                      onClick={() => {
                        if (activeBatchIndex > 0)
                          switchBatchScript(activeBatchIndex - 1);
                      }}
                      title="Previous script"
                    >
                      ←
                    </button>
                    <select
                      className="studio-script-select"
                      value={activeBatchIndex !== null ? activeBatchIndex : ""}
                      onChange={(e) =>
                        switchBatchScript(Number(e.target.value))
                      }
                    >
                      {batchItems.map((item, idx) => (
                        <option
                          key={`batch-opt-${idx}`}
                          value={idx}
                          disabled={!item.marking}
                        >
                          {`Script ${idx + 1}/${batchItems.length}: ${item.learnerName || item.fileName}`}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn-studio-arrow"
                      disabled={activeBatchIndex >= batchItems.length - 1}
                      onClick={() => {
                        if (activeBatchIndex < batchItems.length - 1)
                          switchBatchScript(activeBatchIndex + 1);
                      }}
                      title="Next script"
                    >
                      →
                    </button>
                  </div>
                )}
                <div className="studio-info-tags">
                  <span className="studio-tag">
                    {selectedSubject
                      ? `${selectedSubject.name} (Gr ${selectedSubject.grade})`
                      : "Subject"}
                  </span>
                  <span className="studio-tag">Term {selection.term}</span>
                  <span className="studio-tag">
                    {selectedAssessment
                      ? selectedAssessment.section_name
                      : "Assessment"}
                  </span>
                  {marking?.learnerName && (
                    <span
                      className="studio-tag learner-tag"
                      title="Learner name detected on script"
                    >
                      Detected: {marking.learnerName}
                    </span>
                  )}
                  <select
                    ref={learnerSelectRef}
                    className={`studio-script-select${!selection.studentId && approved ? " needs-selection" : ""}`}
                    aria-label="Assigned learner"
                    value={selection.studentId}
                    onChange={(e) => {
                      const nextStudentId = e.target.value;
                      setSelection((current) => ({
                        ...current,
                        studentId: nextStudentId,
                      }));
                      setError("");
                      if (batchMode && activeBatchIndex !== null) {
                        setBatchItems((current) =>
                          current.map((item, idx) =>
                            idx === activeBatchIndex
                              ? {
                                  ...item,
                                  studentId: nextStudentId,
                                  status: nextStudentId
                                    ? "matched"
                                    : "needs_confirmation",
                                }
                              : item,
                          ),
                        );
                      }
                      setApproved(false); setSaveStatus('');
                      setMarking(current => current ? { ...current, saved: false } : current);
                    }}
                  >
                    <option value="">
                      {marking?.learnerName
                        ? `Confirm learner (${marking.learnerName})…`
                        : "Select learner to save…"}
                    </option>
                    {learners.map((learner) => (
                      <option
                        key={learner.student_id}
                        value={learner.student_id}
                      >
                        {learner.first_name} {learner.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="studio-header-right">
                <div className="studio-score-badge">
                  <strong>
                    {Number.isInteger(Number(marking.totalAwarded))
                      ? marking.totalAwarded
                      : Number(marking.totalAwarded).toFixed(2)}{" "}
                    /{" "}
                    {Number.isInteger(Number(marking.totalMarks))
                      ? marking.totalMarks
                      : Number(marking.totalMarks).toFixed(2)}
                  </strong>
                  <span>({percentage.toFixed(1)}%)</span>
                </div>
                <span
                  className={`studio-status-pill ${reviewQuestions > 0 ? "warning" : "success"}`}
                >
                  {reviewQuestions > 0
                    ? `${reviewQuestions} need review`
                    : "All checked"}
                </span>
                <label className="studio-approval-checkbox">
                  <input
                    type="checkbox"
                    checked={approved}
                    onChange={(event) => {
                      const isChecked = event.target.checked;
                      setApproved(isChecked);
                      if (isChecked) {
                        prepareApprovedState(); setError('');
                        setSaveStatus(selection.studentId ? 'Ready to save. Click Save reviewed marks to update the learner record.'
                          : 'Select the learner, review the marks, then click Save reviewed marks.');
                        if (!selection.studentId) learnerSelectRef.current?.focus();
                      } else {
                        setSaveStatus("");
                      }
                    }}
                  />
                  <span>I've checked these marks</span>
                </label>
                <button
                  className={`studio-save-btn${marking.saved ? " is-saved" : ""}`}
                  type="button"
                  disabled={savingMarks || loading || !approved || !selection.studentId || marking.saved}
                  onClick={() => saveApprovedMarks()}
                >
                  {savingMarks
                    ? "Saving…"
                    : marking.saved
                      ? "✓ Saved"
                      : "Approve & save marks"}
                </button>
                <HelpTip label="About approving marks" align="end">
                  Nothing goes into your mark book until you approve. Your review is saved in
                  this browser, so you can stop and come back to it later.
                </HelpTip>
                <TourButton onClick={reviewTour.start}>Tour</TourButton>
                {/* Secondary actions live in one menu so the save action stands out. */}
                <details className="studio-more-menu">
                  <summary>More</summary>
                  <div className="studio-more-list">
                {batchMode && batchItems.length > 1 && (
                  <div className="studio-batch-download-actions">
                    <button
                      type="button"
                      className="btn-studio-batch-download"
                      onClick={downloadAllBatchMarkedZip}
                      disabled={bulkDownloadState.loading}
                      title="Download all marked student scripts as a single ZIP file"
                    >
                      {bulkDownloadState.loading
                        ? bulkDownloadState.progress || "Processing ZIP…"
                        : `Download all (${batchSummary.processed} marked ZIP)`}
                    </button>
                    <button
                      type="button"
                      className="btn-studio-batch-combined"
                      onClick={downloadAllBatchCombinedPdf}
                      disabled={bulkDownloadState.loading}
                      title="Download all marked scripts merged into one combined PDF for 1-click printing"
                    >
                      Combined PDF
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="btn-toggle-setup"
                  onClick={() => setSetupCollapsed((cur) => !cur)}
                >
                  {setupCollapsed ? "Edit files" : "Hide files"}
                </button>
                  </div>
                </details>
              </div>
            </header>
          ) : (
            <header className="marking-pre-marking-header">
              <div className="pre-header-info">
                <span className="pre-file-badge">Learner paper</span>
                <span className="pre-file-name">
                  {activePaperFile?.name || "Uploaded document"}
                </span>
                {batchMode && examFiles.length > 1 && (
                  <div className="pre-batch-nav">
                    <button
                      type="button"
                      className="btn-studio-arrow"
                      disabled={(activeBatchIndex || 0) === 0}
                      onClick={() => {
                        const nextIdx = Math.max(
                          0,
                          (activeBatchIndex || 0) - 1,
                        );
                        setActiveBatchIndex(nextIdx);
                        setPaperFile(examFiles[nextIdx]);
                      }}
                      title="Previous script"
                    >
                      ←
                    </button>
                    <span className="page-indicator">
                      Script {(activeBatchIndex || 0) + 1} of {examFiles.length}
                    </span>
                    <button
                      type="button"
                      className="btn-studio-arrow"
                      disabled={(activeBatchIndex || 0) >= examFiles.length - 1}
                      onClick={() => {
                        const nextIdx = Math.min(
                          examFiles.length - 1,
                          (activeBatchIndex || 0) + 1,
                        );
                        setActiveBatchIndex(nextIdx);
                        setPaperFile(examFiles[nextIdx]);
                      }}
                      title="Next script"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
              <div className="pre-header-actions">
                {loading ? (
                  <span className="studio-tag">
                    Marking in progress ·{" "}
                    {batchMode && batchProgress.total > 1
                      ? `${Math.min(98, Math.round(((batchProgress.current + loadingState.percent / 100) / batchProgress.total) * 100))}%`
                      : `${loadingState.percent}%`}{" "}
                    ({loadingState.elapsedSeconds}s)
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn-pre-reset"
                    onClick={() => {
                      setPaperFile(null);
                      setExamFile(null);
                      setExamFiles([]);
                      setSinglePdfInfo(null);
                      setBatchItems([]);
                      setMarking(null);
                    }}
                  >
                    Choose different script
                  </button>
                )}
              </div>
            </header>
          )}

          {marking && (error || saveStatus) && (
            <div
              className={`studio-feedback-bar ${error ? "is-error" : marking.saved ? "is-saved" : "is-info"}`}
              role={error ? "alert" : "status"}
            >
              <span>{error || saveStatus}</span>
              {error && !selection.studentId && (
                <button
                  type="button"
                  className="studio-feedback-action"
                  onClick={() => learnerSelectRef.current?.focus()}
                >
                  Select learner
                </button>
              )}
            </div>
          )}

          <div className="marking-review-layout">
            <div className="marking-review-paper">
              <MarkingPaperAnnotations
                file={activePaperFile}
                annotations={paperAnnotations}
                marking={marking}
                learnerName={
                  selectedLearner
                    ? `${selectedLearner.first_name} ${selectedLearner.last_name}`
                    : marking?.learnerName || marking?.learner || ""
                }
                schoolName={marking?.school || ""}
                subjectName={
                  marking?.subjectName || marking?.subject || (selectedSubject ? `${selectedSubject.name} (Grade ${selectedSubject.grade})` : "")
                }
                assessmentName={
                  selectedAssessment
                    ? `${selectedAssessment.section_name} (Term ${selection.term})`
                    : ""
                }
                activeQuestionNumber={activeQuestionNumber}
                targetPage={targetPage}
                readOnly={loading || !marking}
                onSelectQuestion={handleSelectQuestionFromPaper}
                onChange={(nextAnnotations) => {
                  setApproved(false);
                  setPaperAnnotations(nextAnnotations);
                  setSaveStatus("");
                  setMarking((current) =>
                    current && current.saved
                      ? { ...current, saved: false }
                      : current,
                  );
                  if (batchMode && activeBatchIndex !== null) {
                    setBatchItems((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === activeBatchIndex
                          ? { ...item, annotations: nextAnnotations }
                          : item,
                      ),
                    );
                  }
                }}
              />
            </div>
            <div className="marking-review-results">
              {loading ? (
                (() => {
                  const displayPercent =
                    batchMode && batchProgress.total > 1
                      ? Math.min(
                          98,
                          Math.max(
                            5,
                            Math.round(
                              ((batchProgress.current +
                                loadingState.percent / 100) /
                                batchProgress.total) *
                                100,
                            ),
                          ),
                        )
                      : Math.min(98, Math.max(4, loadingState.percent));
                  const pipelineSteps = [
                    "Read the learner script",
                    "Prepare the memo or rubric",
                    "Evaluate the answers against the memo",
                    "Prepare paper marks and learner matching",
                  ];
                  return (
                    <div
                      className="marking-loading-card"
                      role="status"
                      aria-live="polite"
                    >
                      <AiGeneratingLoader />
                      <div className="loading-progress-header">
                        <div className="loading-progress-title">
                          <div className="marking-spinner-sm" />
                          <div>
                            <h3>Marking script with AI</h3>
                            <p className="loading-stage-label">
                              {loadingState.stageLabel}
                            </p>
                          </div>
                        </div>
                        <div className="loading-progress-metrics">
                          <strong className="loading-percent-value">
                            {displayPercent}%
                          </strong>
                          <span className="loading-elapsed-time">
                            {loadingState.elapsedSeconds}s elapsed
                          </span>
                        </div>
                      </div>

                      <div
                        className="loading-progress-bar-track"
                        role="progressbar"
                        aria-valuenow={displayPercent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="loading-progress-bar-fill"
                          style={{ width: `${displayPercent}%` }}
                        />
                      </div>

                      {batchMode && batchProgress.total > 1 && (
                        <div className="loading-batch-meta">
                          <span>
                            Class batch: {batchProgress.current} of{" "}
                            {batchProgress.total} scripts completed
                          </span>
                          <span>Running up to {concurrency} scripts in parallel</span>
                        </div>
                      )}

                      <ol className="loading-pipeline-steps">
                        {pipelineSteps.map((stepText, idx) => {
                          const isDone = idx < loadingState.stepIndex;
                          const isCurrent = idx === loadingState.stepIndex;
                          return (
                            <li
                              key={stepText}
                              className={
                                isDone
                                  ? "is-done"
                                  : isCurrent
                                    ? "is-active"
                                    : "is-pending"
                              }
                            >
                              <span
                                className="step-indicator-icon"
                                aria-hidden="true"
                              >
                                {isDone ? "✓" : isCurrent ? "●" : "○"}
                              </span>
                              <span>{stepText}</span>
                            </li>
                          );
                        })}
                      </ol>

                      {batchMode && batchItems.length > 0 && (
                        <div className="loading-completed-feed">
                          <strong>
                            Scripts ({batchItems.filter((item) => item.marking || item.status === "skipped").length} of {batchItems.length} done)
                          </strong>
                          <ul>
                            {batchItems.slice(-4).map((item, idx) => (
                              <li key={`${item.fileName}-${idx}`}>
                                <span>{item.status === "pending" ? item.fileName : item.learnerName || item.fileName}</span>
                                {item.status === "pending" ? (
                                  <span className="feed-waiting">Waiting…</span>
                                ) : item.status === "skipped" ? (
                                  <span className="feed-waiting">Skipped (no answers)</span>
                                ) : item.marking ? (
                                  <strong>
                                    {item.marking.totalAwarded}/
                                    {item.marking.totalMarks} (
                                    {item.marking.totalMarks > 0
                                      ? (
                                          (item.marking.totalAwarded /
                                            item.marking.totalMarks) *
                                          100
                                        ).toFixed(0)
                                      : 0}
                                    %)
                                  </strong>
                                ) : (
                                  <span className="feed-failed">Failed</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <p className="marking-progress-hint">
                        You can inspect and browse pages of the learner script
                        on the left while marking finishes.
                      </p>
                    </div>
                  );
                })()
              ) : marking && setupCollapsed ? (
                <AutomatedMarkingResults
                  marking={marking}
                  batchItems={batchMode ? batchItems : []}
                  learnerName={
                    selectedLearner
                      ? `${selectedLearner.first_name} ${selectedLearner.last_name}`
                      : marking?.learnerName || marking?.learner || ""
                  }
                  schoolName={marking?.school || ""}
                  subjectName={
                    marking?.subjectName || marking?.subject || (selectedSubject ? `${selectedSubject.name} (Grade ${selectedSubject.grade})` : "")
                  }
                  assessmentName={
                    selectedAssessment
                      ? `${selectedAssessment.section_name} (Term ${selection.term})`
                      : ""
                  }
                  onChange={updateResult}
                  activeQuestionIndex={activeQuestionIndex}
                  onSelectQuestionIndex={selectReviewQuestion}
                  onJumpToQuestion={(questionNumber) => setTargetPage({ questionNumber, ts: Date.now() })}
                  onJumpToPage={(pageNum) =>
                    setTargetPage({ page: pageNum, ts: Date.now() })
                  }
                />
              ) : !marking && batchMode && batchItems.length > 0 ? (
                <section className="batch-progress-card">
                  <div className="marking-results-heading">
                    <div>
                      <p className="eyebrow">Batch progress</p>
                      <h2>
                        {batchProgress.current} of {batchProgress.total}{" "}
                        processed
                      </h2>
                    </div>
                    {batchSummary.processed > 0 && (
                      <div className="struggle-export-actions">
                        <button
                          type="button"
                          className="btn-batch-download-zip"
                          onClick={downloadAllBatchMarkedZip}
                          disabled={bulkDownloadState.loading}
                          title="Download all marked student scripts as a single ZIP file"
                        >
                          {bulkDownloadState.loading
                            ? bulkDownloadState.progress || "Processing ZIP…"
                            : `Download all (${batchSummary.processed} marked ZIP)`}
                        </button>
                        <button
                          type="button"
                          className="btn-batch-download-combined"
                          onClick={downloadAllBatchCombinedPdf}
                          disabled={bulkDownloadState.loading}
                          title="Download all marked scripts merged into one combined PDF for 1-click printing"
                        >
                          Combined PDF (Print all)
                        </button>
                        <button
                          type="button"
                          className="btn-export-struggles"
                          onClick={() => {
                            const firstMarked =
                              batchItems.find((it) => it.marking)?.marking ||
                              null;
                            exportStruggleAreasExcel(firstMarked, batchItems, {
                              learnerName: "Class Batch",
                              subjectName: selectedSubject
                                ? `${selectedSubject.name} (Grade ${selectedSubject.grade})`
                                : "",
                              assessmentName: selectedAssessment
                                ? `${selectedAssessment.section_name} (Term ${selection.term})`
                                : "",
                            });
                          }}
                        >
                          Export struggle Excel
                        </button>
                        <button
                          type="button"
                          className="btn-export-struggles primary"
                          onClick={() => {
                            const firstMarked =
                              batchItems.find((it) => it.marking)?.marking ||
                              null;
                            exportStruggleAreasPdf(firstMarked, batchItems, {
                              learnerName: "Class Batch",
                              subjectName: selectedSubject
                                ? `${selectedSubject.name} (Grade ${selectedSubject.grade})`
                                : "",
                              assessmentName: selectedAssessment
                                ? `${selectedAssessment.section_name} (Term ${selection.term})`
                                : "",
                            });
                          }}
                        >
                          Export struggle PDF
                        </button>
                      </div>
                    )}
                  </div>
                  {bulkDownloadState.loading && (
                    <div className="studio-feedback-bar is-info" role="status">
                      <span>{bulkDownloadState.progress || "Generating marked script files…"}</span>
                    </div>
                  )}
                  {bulkDownloadState.error && (
                    <div className="studio-feedback-bar is-error" role="alert">
                      <span>{bulkDownloadState.error}</span>
                    </div>
                  )}
                  {batchSummary.processed > 0 && (
                    <div
                      className="batch-statistics"
                      aria-label="Batch marking statistics"
                    >
                      <div>
                        <strong>{batchSummary.processed}</strong>
                        <span>Scripts processed</span>
                      </div>
                      <div>
                        <strong>
                          {batchSummary.maximum > 0
                            ? (
                                (batchSummary.awarded / batchSummary.maximum) *
                                100
                              ).toFixed(1)
                            : "0.0"}
                          %
                        </strong>
                        <span>Combined percentage</span>
                      </div>
                      <div>
                        <strong>
                          {batchSummary.percentages.length
                            ? (
                                batchSummary.percentages.reduce(
                                  (sum, value) => sum + value,
                                  0,
                                ) / batchSummary.percentages.length
                              ).toFixed(1)
                            : "0.0"}
                          %
                        </strong>
                        <span>Average script</span>
                      </div>
                      <div>
                        <strong>
                          {batchSummary.percentages.length
                            ? Math.max(...batchSummary.percentages).toFixed(1)
                            : "0.0"}
                          %
                        </strong>
                        <span>Highest script</span>
                      </div>
                    </div>
                  )}
                  <div className="batch-list">
                    {batchItems.map((item, index) => (
                      <div
                        className={`batch-item ${item.status}`}
                        key={`${item.fileName}-${index}`}
                      >
                        <div>
                          <strong>{item.learnerName}</strong>
                          <span>{item.fileName}</span>
                        </div>
                        <span>
                          {item.status === "matched"
                            ? "Matched - ready for review"
                            : item.status === 'pending' ? 'Waiting to mark'
                            : item.status === 'skipped' ? item.reason || 'Skipped'
                            : item.status === "failed"
                              ? item.error
                              : "Needs learner confirmation"}
                        </span>
                        {item.status === "needs_confirmation" && (
                          <select
                            value={item.studentId}
                            onChange={(event) =>
                              setBatchItems((current) =>
                                current.map((candidate, candidateIndex) =>
                                  candidateIndex === index
                                    ? {
                                        ...candidate,
                                        studentId: event.target.value,
                                        status: event.target.value
                                          ? "matched"
                                          : "needs_confirmation",
                                      }
                                    : candidate,
                                ),
                              )
                            }
                          >
                            <option value="">Assign learner</option>
                            {learners.map((learner) => (
                              <option
                                key={learner.student_id}
                                value={learner.student_id}
                              >
                                {learner.first_name} {learner.last_name}
                              </option>
                            ))}
                          </select>
                        )}
                        {item.marking && (
                          <button
                            type="button"
                            onClick={() => switchBatchScript(index)}
                          >
                            Review marks
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
                <div className="marking-form-wrapper">
                  {marking && (
                    <div className="form-return-bar">
                      <button
                        type="button"
                        className="btn-toggle-setup"
                        onClick={() => setSetupCollapsed(true)}
                      >
                        ← Return to marking review
                      </button>
                    </div>
                  )}
                  {renderSetupForm()}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <FeatureTour steps={SETUP_TOUR} open={setupTour.open && !isStudioActive} onClose={setupTour.close} />
      <FeatureTour steps={REVIEW_TOUR} open={reviewTour.open && Boolean(marking)} onClose={reviewTour.close} />
    </main>
  );
}
